import { UITable } from '../../components/table/ui-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { compareValuesAsStrings } from '../../global/comparators';
import { Labels } from '../../global/labels';
import {
    adoptStyle,
    closestByCond,
    dispatchCustomEvent,
    findNodesBetween,
    updateClassList,
    updateElement,
} from '../../global/ui-helpers';
import type { NullOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-table-sortable.css';
import type {
    UITableCompareFunction,
    UITableSortEventDetails,
} from './ui-table-sortable.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-table sortable plugin.
 * @alias UITableSortable
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableSortable implements ComponentPlugin {
    private readonly target: UITable;

    constructor(target: UITable) {
        this.target = target;
    }

    static readonly SORT_ASC = 1;
    static readonly SORT_DESC = -1;

    /**
     * @type {TranslationLabels.Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-table-sortable', {
            sortBy: 'Sort by',
            clickToSort: 'Click to sort',
        });
    }

    /**
     * Creates missing elements and assigns events for table to be fully
     * sortable.
     * @private
     */
    private buildSortable(): void {
        if (!this.canBuildSortable()) {
            return;
        }
        const links = this.getSortableLinks();
        if (!links?.length) {
            return;
        }

        links.forEach((link) => {
            const th = this.getClosestColumn(link);
            let shouldPlaceButton = true;

            if (link.localName !== 'th') {
                updateClassList(th, {
                    'sort': true,
                    '-asc': link.classList.contains('-asc'),
                    '-desc': link.classList.contains('-desc'),
                    '-sorted': link.classList.contains('-sorted'),
                });
                updateClassList(link, {
                    'sort': false,
                    '-asc': false,
                    '-desc': false,
                    '-sorted': false,
                });
                if (link.childNodes.length === 0) {
                    link.parentElement?.removeChild(link);
                }
            }

            if (
                th.firstElementChild instanceof HTMLElement &&
                ['button', 'a'].includes(th.firstElementChild.localName)
            ) {
                shouldPlaceButton = false;
            }

            if (shouldPlaceButton) {
                const button = this.target.createElement({
                    tagName: 'button',
                    attributes: {
                        'type': 'button',
                        'aria-description': UITableSortable.labels.clickToSort,
                    },
                    children: Array.from(th.childNodes).filter(
                        (child) =>
                            !(child instanceof Element) ||
                            child.localName !== 'ui-tooltip'
                    ),
                });
                th.insertAdjacentElement('afterbegin', button);
            }

            this.setAccessibilityAttrs(th);
        });
    }

    /**
     * Checks if table has thead and tbody and can build sortable.
     * @returns {boolean}
     * @private
     */
    private canBuildSortable(): boolean {
        return !!(
            this.target.table.tHead || this.target.table.tBodies.length > 0
        );
    }

    /**
     * Handles event for sorting button clicks by checking direction of sorting.
     * @param {Event} event
     * @private
     */
    private handleTableSort(event: Event): void {
        const link = event.target as HTMLElement;
        // If tooltip is clicked do not sort.
        const isTooltip = closestByCond(
            link,
            (node) => node instanceof Element && node.localName === 'ui-tooltip'
        );
        if (isTooltip) {
            return;
        }
        const th = this.getClosestColumn(link);
        const direction = this.getNextDirection(th);
        this.sort(th, direction, null);

        const sortEventDetails: UITableSortEventDetails = {
            column: th,
            columnId: th.id || null,
            direction: direction === UITableSortable.SORT_ASC ? 'asc' : 'desc',
            directionCode: direction,
        };

        dispatchCustomEvent(this.target, 'tablesort', sortEventDetails);
    }

    /**
     * Gets the closest column header element by checking an argument.
     * @private
     * @param {Element} element
     * @returns {HTMLTableCellElement}
     */
    private getClosestColumn(element: Element): HTMLTableCellElement {
        if (element.localName === 'th') {
            return element as HTMLTableCellElement;
        }

        return closestByCond(
            element,
            (node) => node instanceof Element && node.localName === 'th'
        ) as HTMLTableCellElement;
    }

    /**
     * Returns sorting direction by checking current direction of an element
     * and reversing it.
     * @private
     * @param {HTMLElement} element
     * @returns {number} Direction of sorting either 1 for ascending or -1 for descending.
     */
    private getNextDirection(element: Element): 1 | -1 {
        const hasSorted = element.classList.contains('-sorted');
        return (!hasSorted && element.classList.contains('-asc')) ||
            (hasSorted && element.classList.contains('-desc'))
            ? UITableSortable.SORT_ASC
            : UITableSortable.SORT_DESC;
    }

    /**
     * Extracts sortable text content from a table cell,
     * excluding injected invert-plugin label containers.
     * @param {HTMLTableCellElement} cell
     * @returns {string}
     * @private
     */
    private getCellSortValue(cell: Element): string {
        const labelText =
            cell.querySelector('.-cell-label-container')?.textContent ?? '';
        return cell.textContent
            .slice(labelText.length)
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Sorts table by provided th element from thead or by column number.
     * @param {HTMLTableCellElement|number} column - TH element or it's index.
     * from THEAD that needs to be sorted or numerical index of column
     * @param {number} [direction] - Ascending value is 1. Descending value is -1.
     * @param {Function} [compareFn] - Function that compares two values for sorting
     */
    sort(
        column: HTMLTableCellElement | number,
        direction: 1 | -1 = 1,
        compareFn: UITableCompareFunction = null
    ): void {
        const index = typeof column === 'number' ? column : column.cellIndex;
        const th =
            column instanceof HTMLTableCellElement
                ? column
                : this.getColumnHeadByIndex(index);
        if (!th) {
            return;
        }
        const rows = this.getSortableRows();
        rows.sort((a, b) => {
            const x = this.getCellSortValue(a.children[index]);
            const y = this.getCellSortValue(b.children[index]);
            return (
                (typeof compareFn === 'function'
                    ? compareFn(x, y)
                    : compareValuesAsStrings(x, y)) * direction
            );
        });

        for (const row of rows) {
            const parent = row.parentNode!;
            const sibling = row.nextElementSibling;
            const rowElement = parent.removeChild(row);
            parent.appendChild(rowElement);

            // Process expandable rows.
            if (sibling?.classList.contains('-extra')) {
                rowElement.insertAdjacentElement('afterend', sibling);
            }
        }
        this.processSortedColumn(th, direction);
    }

    /**
     * Returns all rows that are available to sort in the table. Colspans and rowspans
     * aren't sortable.
     * @private
     * @returns {Array<HTMLTableRowElement>}
     */
    private getSortableRows(): Array<HTMLTableRowElement> {
        if (!this.target.table.tBodies?.[0]) {
            return [];
        }
        return Array.from(this.target.table.tBodies[0].rows).filter((row) => {
            return !this.checkUnsuitableCells(row);
        });
    }

    /**
     * Returns column head element that has provided index in the thead element.
     * @private
     * @param {number} index
     * @returns {HTMLTableCellElement}
     */
    private getColumnHeadByIndex(index: number): NullOr<HTMLTableCellElement> {
        if (this.target.table.tHead) {
            return this.target.table.tHead.rows[0].cells[index];
        }
        return null;
    }

    /**
     * Applies required markings to newly sorted column and removes sorting marks
     * from other columns.
     * @private
     * @param {HTMLTableCellElement} th
     * @param {number} order
     */
    private processSortedColumn(th: HTMLTableCellElement, order: number): void {
        const links = this.getSortableLinks();
        links?.forEach((link) => {
            const columnTh = this.getClosestColumn(link);
            columnTh.removeAttribute('aria-sort');
            updateElement(columnTh, {
                classList: {
                    '-sorted': false,
                },
            });
        });
        updateElement(th, {
            classList: {
                '-sorted': true,
                '-asc': order === UITableSortable.SORT_ASC,
                '-desc': order === UITableSortable.SORT_DESC,
            },
        });
        this.setAccessibilityAttrs(th);
    }

    /**
     * Sets accessibility attributes.
     * @param {HTMLTableCellElement} th
     * @private
     */
    private setAccessibilityAttrs(th: HTMLTableCellElement): void {
        let dir: string | undefined;
        // Follow the same logic as in CSS - only define a column as sorted if it has both .-asc/.desc and .-sorted classes
        if (th.classList.contains('-asc') && th.classList.contains('-sorted')) {
            dir = 'ascending';
        }
        if (
            th.classList.contains('-desc') &&
            th.classList.contains('-sorted')
        ) {
            dir = 'descending';
        }
        if (dir !== undefined) {
            th.setAttribute('aria-sort', dir);
        }
    }

    /**
     * Returns list of elements that are linked to column sorting
     * in the thead element.
     * @private
     * @returns {NodeListOf<HTMLElement>} Links with .sort classname
     */
    private getSortableLinks(): NodeListOf<Element> | undefined {
        return this.target.table.tHead?.rows[0].querySelectorAll('.sort');
    }

    /**
     * Checks if table includes any unsupported types of cell, like those
     * spanned by row or column.
     * @param {HTMLTableRowElement} tr
     * @returns {boolean}
     */
    checkUnsuitableCells(tr: HTMLTableRowElement): boolean {
        return (
            tr.querySelector('[colspan], [rowspan]') instanceof
            HTMLTableCellElement
        );
    }

    /**
     * Builds compact sortable interface for smaller screens.
     */
    buildCompactSortable(): void {
        if (!this.canBuildSortable()) {
            return;
        }
        const spoiler = this.target.createElement({
            tagName: 'ui-curtain',
            attributes: {
                layout: 'spoiler',
                label: this.target.label || UITableSortable.labels.sortBy,
            },
        });
        findNodesBetween(this.target, '.sort').forEach((sortElem) => {
            const div = this.target.createElement({
                tagName: 'div',
                attributes: {
                    class: sortElem.className,
                },
                children: Array.from(sortElem.childNodes)
                    .filter(
                        (child) =>
                            !(child instanceof Element) ||
                            child.localName !== 'ui-tooltip'
                    )
                    .map((child) => child.cloneNode(true)),
            });
            spoiler.appendChild(div);
        });
        const mobileSortContainer = this.target.createElement({
            tagName: 'div',
            classList: {
                'ui-table-sortable__sortcontainer': true,
                '-hidden': this.target.table.classList.contains('-auto'),
            },
            children: [spoiler],
        });
        this.target.insertAdjacentElement('afterbegin', mobileSortContainer);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-sortable');
        this.buildSortable();
        this.buildCompactSortable();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        let applyEvents = true;
        if (!this.canBuildSortable()) {
            return;
        }
        const links = this.getSortableLinks();
        if (!links?.length) {
            return;
        }

        links.forEach((link) => {
            const th = this.getClosestColumn(link);
            // <a> tag means external link in the most cases in iBank.
            // Otherwise if need special external sorting behaviour apply -external-sort CSS class to
            // external <button> of any other control. This is mostly made to provide smooth transition
            // between legacy and web-components in iBank.
            if (
                th.firstElementChild?.localName === 'a' ||
                th.firstElementChild?.classList.contains('-external-sort')
            ) {
                applyEvents = false;
            } else {
                th.addEventListener('click', this.handleTableSort.bind(this));
            }
        });

        const sorted = findNodesBetween(this.target.table, 'th.-sorted')[0];
        if (sorted && applyEvents) {
            const order = sorted.classList.contains('-asc')
                ? UITableSortable.SORT_ASC
                : UITableSortable.SORT_DESC;
            this.sort(sorted as HTMLTableCellElement, order);
        }

        const ths = findNodesBetween(this.target.table, '.sort');
        const items = findNodesBetween(
            this.target,
            '.ui-table-sortable__sortcontainer .sort'
        );
        items.forEach((item, i) => {
            item.addEventListener('click', () => {
                items.forEach((elem) => {
                    elem.classList.remove('-sorted');
                });
                const isASC =
                    this.getNextDirection(ths[i]) === UITableSortable.SORT_ASC;
                updateClassList(item, {
                    '-asc': isASC,
                    '-desc': !isASC,
                    '-sorted': true,
                });
                (ths[i] as HTMLElement).click();
            });
        });
    }
}

UITable.registerPlugin('sortable', UITableSortable);
adoptStyle(styles, 'ui-table-sortable-style').catch((err) =>
    console.error(err)
);
